import { useInvoice } from '@/hooks/invoice/useInvoice';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { PaymentOptionSelectorProps } from '../types';
import { PaymentIcon } from '../utils';

const PaymentOptionsSelector = ({ setSelectedPaymentMethod, selectedPaymentMethod }: PaymentOptionSelectorProps) => {
  const { t } = useTranslation();
  const { invoice } = useInvoice();
  const paymentMethods = invoice?.paymentMethods ?? [];
  const remainingAmount = invoice?.remainingAmountInclVAT || 0;

  const getSubtitle = (serviceProvider: string) => {
    switch (serviceProvider?.toLowerCase()) {
      case 'swish':
        return t('components.payment-option-selector.subtitles.swish');
      case 'inveroak':
      case 'stripe':
        return t('components.payment-option-selector.subtitles.credit-card');
      case 'banktransfer':
        return t('components.payment-option-selector.subtitles.bankgiro');
      default:
        return '';
    }
  };

  const getActionText = (serviceProvider: string) => {
    return serviceProvider?.toLowerCase() === 'banktransfer'
      ? t('buttons.two-bank-days')
      : t('common-phrases.instant');
  };

  return (
    <ToggleButtonGroup
      orientation='vertical'
      value={selectedPaymentMethod?.paymentMethodId}
      exclusive
      onChange={(_, value) => value && setSelectedPaymentMethod(value)}
      sx={{ width: '100%', p: 0 }}
    >
      {paymentMethods.map((method, index) => {
        const isSelected = method.paymentMethodId === selectedPaymentMethod?.paymentMethodId;

        return (
          <Stack key={method.paymentMethodId}>
            <ToggleButton
              value={method.paymentMethodId}
              disabled={remainingAmount < method.minimumPaymentAmount}
              sx={{
                p: 0,
                textAlign: 'left',
                border: 'none'
              }}
              focusRipple
            >
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='flex-start'
                gap={2}
                sx={{
                  width: '100%',
                  padding: 2,
                  position: 'relative',
                  border: isSelected ? '1px solid black' : '1px solid transparent'
                }}
              >
                <Stack direction='row' alignItems='center' justifyContent='center'>
                  <PaymentIcon size='medium' provider={method.serviceProvider} alt={method.methodText} />
                </Stack>

                <Stack flex={1} gap={0.5}>
                  <Typography variant='body2'>{method.methodText}</Typography>
                  <Typography variant='body3'>{getSubtitle(method.serviceProvider)}</Typography>
                </Stack>

                <Chip
                  color={isSelected ? 'primary' : 'default'}
                  label={getActionText(String(method.serviceProvider))}
                />
              </Stack>
            </ToggleButton>
            {index !== paymentMethods.length - 1 && (
              <Divider
                flexItem
                sx={{
                  borderWidth: '1px'
                }}
              />
            )}
          </Stack>
        );
      })}
    </ToggleButtonGroup>
  );
};

export default PaymentOptionsSelector;
